//@flow
import {ActionCreator, createReducer} from '../../store/utils';

import type {
  BlogCategory,
  BlogPost,
  BlogPostDetail,
  BlogAuthor,
} from '../../entities';
import type {BlogPostFilter} from './api/blog-post';
export type BlogState = {
  categories: BlogCategory[],
  blogPosts?: BlogPost[],
  currentBlogPost?: BlogPostDetail,
  blogPostFilter?: BlogPostFilter,
  pagination: {
    page: number,
    size: number,
    total: number,
  },
  authors?: BlogAuthor[],
  categoryModalOpen?: boolean,
};

export type BlogActions = {
  categories?: BlogCategory[],
  category?: BlogCategory,
  categoryId?: string,

  blogPosts?: BlogPost[],
  blogPost?: BlogPost,
  blogPostId?: string,

  blogPostFilter?: BlogPostFilter,
  pagination?: {
    page: number,
    size: number,
    total: number,
  },
  authors?: BlogAuthor[],

  openCategoryModal?: boolean,
};

export const initialState: BlogState = {
  categories: [],
  blogPosts: undefined,
  pagination: {
    page: 0,
    size: 10,
    total: 0,
  },
};

export class SetBlogAuthors extends ActionCreator<BlogActions> {}

export class SetBlogPagination extends ActionCreator<BlogActions> {}

export class SetSavedCategories extends ActionCreator<BlogActions> {}
export class AddCategory extends ActionCreator<BlogActions> {}
export class UpdateCategory extends ActionCreator<BlogActions> {}
export class DeleteCategoryBlog extends ActionCreator<BlogActions> {}

export class SetSavedBlogPosts extends ActionCreator<BlogActions> {}
export class AddBlogPost extends ActionCreator<BlogActions> {}
export class UpdateBlogPost extends ActionCreator<BlogActions> {}
export class DeleteBlogPost extends ActionCreator<BlogActions> {}
export class ToggleBlogPostPublish extends ActionCreator<BlogActions> {}
export class ToggleBlogPostFeatured extends ActionCreator<BlogActions> {}

export class SetCurrentBlogPost extends ActionCreator<BlogActions> {}
export class ClearCurrentBlogPost extends ActionCreator<BlogActions> {}

export class SetBlogPostFilter extends ActionCreator<BlogActions> {}

export class SetCategoriesModalOpen extends ActionCreator<BlogActions> {}

export const actions = {
  setSavedCategories: (categories: BlogCategory[]) =>
    new SetSavedCategories({categories}),
  setBlogAuthors: (authors: BlogAuthor[]) => new SetBlogAuthors({authors}),
  setBlogPagination: (pagination: {
    page: number,
    size: number,
    total: number,
  }) => new SetBlogPagination({pagination}),
  addCategory: (category: BlogCategory) => new AddCategory({category}),
  updateCategory: (category: BlogCategory) => new UpdateCategory({category}),
  deleteBlogCategory: (categoryId: string) =>
    new DeleteCategoryBlog({categoryId}),

  setSavedBlogPosts: (blogPosts: BlogPost[]) =>
    new SetSavedBlogPosts({blogPosts}),
  addBlogPost: (blogPost: BlogPost) => new AddBlogPost({blogPost}),
  updateBlogPost: (blogPost: BlogPost) => new UpdateBlogPost({blogPost}),
  deleteBlogPost: (blogPostId: string) => new DeleteBlogPost({blogPostId}),
  toggleBlogPostPublish: (blogPostId: string) =>
    new ToggleBlogPostPublish({blogPostId}),
  toggleBlogPostFeatured: (blogPostId: string) =>
    new ToggleBlogPostFeatured({blogPostId}),

  setCurrentBlogPost: (blogPost: BlogPostDetail) =>
    new SetCurrentBlogPost({blogPost}),

  clearCurrentBlogPost: () => new ClearCurrentBlogPost({}),
  setSearchBlogPostFilter: (title: string) =>
    new SetBlogPostFilter({
      blogPostFilter: {
        title,
      },
    }),

  setEnabledBlogPostFilter: (enabled: boolean) =>
    new SetBlogPostFilter({
      blogPostFilter: {
        enabled,
      },
    }),

  setAuthorsBlogPostFilter: (authors: string[]) =>
    new SetBlogPostFilter({
      blogPostFilter: {
        authors,
      },
    }),

  setCategoriesBlogPostFilter: (categories: string[]) =>
    new SetBlogPostFilter({
      blogPostFilter: {
        categories,
      },
    }),

  openCategoriesModal: () =>
    new SetCategoriesModalOpen({openCategoryModal: true}),
  closeCategoriesModal: () =>
    new SetCategoriesModalOpen({openCategoryModal: false}),
};

export default createReducer(initialState, {
  [SetBlogAuthors.type]: (state, {authors}) => ({
    ...state,
    authors,
  }),
  [SetBlogPagination.type]: (state, {pagination}) => ({
    ...state,
    pagination: {
      ...state.pagination,
      ...pagination,
    },
  }),
  [SetSavedCategories.type]: (state, {categories}) => ({
    ...state,
    categories,
  }),
  [AddCategory.type]: (state, {category}) => ({
    ...state,
    categories: [...state.categories, category],
  }),
  [UpdateCategory.type]: (state, {category}) => ({
    ...state,
    categories: state.categories.map(c =>
      c.id === category.id ? category : c
    ),
  }),
  [DeleteCategoryBlog.type]: (state, {categoryId}) => ({
    ...state,
    categories: state.categories.filter(c => c.id !== categoryId),
  }),
  [SetSavedBlogPosts.type]: (state, {blogPosts}) => ({
    ...state,
    blogPosts,
  }),
  [AddBlogPost.type]: (state, {blogPost}) => {
    const prevBlogPost = state.blogPosts || [];
    return {
      ...state,
      blogPosts: [...prevBlogPost, blogPost],
    };
  },
  [UpdateBlogPost.type]: (state, {blogPost}) => ({
    ...state,
    blogPosts: state.blogPosts.map(b => (b.id === blogPost.id ? blogPost : b)),
  }),
  [DeleteBlogPost.type]: (state, {blogPostId}) => ({
    ...state,
    blogPosts: state.blogPosts.filter(b => b.id !== blogPostId),
  }),
  [SetCurrentBlogPost.type]: (state, {blogPost}) => ({
    ...state,
    currentBlogPost: blogPost,
  }),
  [ClearCurrentBlogPost.type]: state => ({
    ...state,
    currentBlogPost: undefined,
  }),
  [ToggleBlogPostPublish.type]: (state, {blogPostId}) => ({
    ...state,
    blogPosts: state.blogPosts.map(blog => {
      if (blog.id === blogPostId) {
        return {
          ...blog,
          enabled: !blog.enabled,
        };
      }
      return blog;
    }),
  }),
  [SetBlogPostFilter.type]: (state, {blogPostFilter}) => ({
    ...state,
    blogPostFilter: {
      ...state.blogPostFilter,
      ...blogPostFilter,
    },
  }),
  [SetCategoriesModalOpen.type]: (state, {openCategoryModal}) => ({
    ...state,
    categoryModalOpen: openCategoryModal,
  }),
  [ToggleBlogPostFeatured.type]: (state, {blogPostId}) => ({
    ...state,
    blogPosts: state.blogPosts.map(blog => {
      if (blog.id === blogPostId) {
        return {
          ...blog,
          featured: !blog.featured,
        };
      }
      return blog;
    }),
  }),
});
